import { useMemo } from 'react';
import { EMPTY_ARRAY } from '../../../utils';
import { getAgGridColId } from '../columns/getAgGridColId';
import type { ColumnDynamics } from '../columns/types';
import { isColumnGroup, type ColumnOrColumnGroup } from '../types';
import { getColumnDynamicsState, type ColumnDynamicsSpec } from './ColumnDynamics';

function getColumnOrGroupID(column: ColumnOrColumnGroup) {
  return isColumnGroup(column) ? column.groupId : getAgGridColId(column);
}

/**
 * useColumnDynamics is super simple. It just takes in an array of columns plus persisted column dynamics state and specs, and then hydrates
 * the column dynamics stuff onto the input columns array, then returns the hydrated input columns array.
 */
export const useColumnDynamics = ({
  columns,
  persistedColumnDynamics = EMPTY_ARRAY,
  specs,
}: {
  columns: ColumnOrColumnGroup[];
  persistedColumnDynamics: ColumnDynamics[] | undefined;
  specs: ColumnDynamicsSpec[] | undefined;
}) => {
  const specsByType = useMemo(() => (specs ? new Map(specs.map(spec => [spec.type, spec])) : undefined), [specs]);

  return useMemo(() => {
    if (!specsByType) {
      return columns;
    }

    const baseColumnColIDs = new Set(columns.map(getColumnOrGroupID));
    const persistedColumnDynamicsByColID = new Map(persistedColumnDynamics.map(dynamics => [dynamics.colID, dynamics]));
    const persistedColumnDynamicsList = [...persistedColumnDynamicsByColID.values()];
    const hydrateColumn = (column: ColumnOrColumnGroup): ColumnOrColumnGroup => {
      if (isColumnGroup(column)) {
        const childDynamics = column.children.find(child => child.dynamics)?.dynamics;
        if (!childDynamics) {
          return column;
        }

        const spec = specsByType.get(childDynamics.spec);
        const resolvedDynamics = {
          ...childDynamics,
          state: persistedColumnDynamicsByColID.get(column.groupId)?.state ?? getColumnDynamicsState(childDynamics),
        };

        return (
          spec?.getColumn(resolvedDynamics) ?? {
            ...column,
            children: column.children.map(child =>
              child.dynamics?.colID === resolvedDynamics.colID ? { ...child, dynamics: resolvedDynamics } : child
            ),
          }
        );
      }

      if (column.dynamics) {
        const colID = getAgGridColId(column);
        const spec = specsByType.get(column.dynamics.spec);
        const resolvedDynamics = {
          ...column.dynamics,
          state: persistedColumnDynamicsByColID.get(colID)?.state ?? getColumnDynamicsState(column.dynamics),
        };

        if (!spec) {
          return {
            ...column,
            dynamics: resolvedDynamics,
          };
        }

        return {
          ...column,
          ...spec.getColumn(resolvedDynamics),
        };
      }

      return column;
    };

    const hydratedColumns = columns.map(hydrateColumn);

    const userAddedColumns = persistedColumnDynamicsList
      ?.filter(dynamics => !baseColumnColIDs.has(dynamics.colID)) // base columns are already hydrated in the step above
      .flatMap(dynamics => {
        const spec = specsByType.get(dynamics.spec);
        if (!spec) {
          return [];
        }

        return spec.getColumn(dynamics);
      });

    return [...hydratedColumns, ...userAddedColumns];
  }, [columns, persistedColumnDynamics, specsByType]);
};
